import { useCredits } from "@/hooks/useCredits";
import { useAuth } from "@/contexts/AuthContext";
import { Zap, Sparkles, AlertTriangle, XCircle, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

interface ToolBreakdown {
  action_type: string;
  total: number;
}

const ACTION_LABELS: Record<string, string> = {
  "generate-script": "Nexis AI",
  "ai-script-edit": "Script Editor",
  "generate-image-prompts": "Image Prompts",
  "generate-video-prompts": "Video Prompts",
  "monetization-generate-products": "Monetization AI",
  "monetization-build-chapter": "Monetization Build",
  "clip-analysis": "Clipping AI",
  "keyword-search": "Keyword Research",
  "askfint-chat": "AskFint",
  "email-list-builder": "Email List Builder",
  "sales-page-builder": "Sales Page Builder",
  "launch-advisor": "Launch Advisor",
};

export function CreditUsageWidget() {
  const { balance, monthlyAllowance, isUnlimited, isLoading, planTier } = useCredits();
  const { user } = useAuth();
  const [breakdown, setBreakdown] = useState<ToolBreakdown[]>([]);

  useEffect(() => {
    if (!user) return;
    const fetchBreakdown = async () => {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from("credit_transactions")
        .select("action_type, amount")
        .eq("user_id", user.id)
        .lt("amount", 0)
        .gte("created_at", thirtyDaysAgo);

      if (data) {
        const map: Record<string, number> = {};
        data.forEach((t) => {
          const key = t.action_type;
          if (key === "initial-allocation" || key === "monthly-refill") return;
          map[key] = (map[key] || 0) + Math.abs(t.amount);
        });
        const sorted = Object.entries(map)
          .map(([action_type, total]) => ({ action_type, total }))
          .sort((a, b) => b.total - a.total)
          .slice(0, 5);
        setBreakdown(sorted);
      }
    };
    fetchBreakdown();
  }, [user]);

  if (isLoading) return null;

  const usagePercent = monthlyAllowance > 0 ? Math.round((balance / monthlyAllowance) * 100) : 0;
  const creditsUsed = monthlyAllowance - balance;
  const tierName = planTier.charAt(0).toUpperCase() + planTier.slice(1);
  const isLow = !isUnlimited && balance > 0 && balance <= 20;
  const isEmpty = !isUnlimited && balance <= 0;

  return (
    <div className="glass-card p-6 border-primary/20 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
          <Zap className="w-5 h-5 text-primary-foreground" />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-xl font-semibold">Credits</h2>
          <p className="text-sm text-muted-foreground">{tierName} Plan</p>
        </div>
        {!isUnlimited && (
          <Link to="/checkout" className="text-sm text-primary hover:underline">
            Upgrade
          </Link>
        )}
      </div>

      {/* Warning banners */}
      {isEmpty && (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-destructive/10 border border-destructive/20">
          <XCircle className="w-5 h-5 text-destructive flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-destructive">Out of credits</p>
            <p className="text-xs text-muted-foreground">Upgrade your plan to continue using AI tools.</p>
          </div>
          <Link to="/checkout" className="text-xs font-semibold text-primary hover:underline whitespace-nowrap">
            Upgrade →
          </Link>
        </div>
      )}

      {isLow && !isEmpty && (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
          <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-500">Running low on credits</p>
            <p className="text-xs text-muted-foreground">You have {balance} credits remaining. Consider upgrading.</p>
          </div>
        </div>
      )}

      {isUnlimited ? (
        <div className="flex items-center gap-2 p-4 rounded-xl bg-muted/50">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <span className="font-semibold text-foreground">Unlimited Credits</span>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {balance.toLocaleString()} remaining of {monthlyAllowance.toLocaleString()}
            </span>
            <span className="text-sm font-medium text-foreground">{Math.min(usagePercent, 100)}%</span>
          </div>
          <Progress value={Math.min(usagePercent, 100)} className="h-2" />
          {creditsUsed > 0 && (
            <p className="text-xs text-muted-foreground">
              {creditsUsed.toLocaleString()} credits used this month
            </p>
          )}
        </div>
      )}

      {/* Tool breakdown */}
      {breakdown.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-border/50">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Usage by tool (30 days)</span>
          </div>
          <div className="space-y-1.5">
            {breakdown.map((item) => (
              <div key={item.action_type} className="flex items-center justify-between text-xs">
                <span className="text-foreground/80">{ACTION_LABELS[item.action_type] || item.action_type}</span>
                <span className="text-muted-foreground font-mono">{item.total} credits</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
